<template>
    <div class="grid-wrapper margin--2xl">
        <NewsFilters
            :category="filters.category"
            :subcategory="filters.subcategory"
            :search="filters.search"
            :categories="config.categories"
            :has-filters="hasFilters"
            :page-url="config.pageUrl"
            :icons="config.icons"
            :i18n="config.i18n"
            @update:category="applyCategory"
            @update:subcategory="applySubcategory"
            @update:search="applyFilter('search', $event)"
            @reset="reset"
        />
    </div>

    <template v-if="items.length">

        <div class="grid-wrapper margin--m">
            <p class="body--small color--accent-color-dark-grey" aria-live="polite">
                {{ countLabel }}
            </p>
        </div>

        <section class="grid-wrapper first_news" id="content">
            <ul class="block__holder">
                <li
                    v-for="(item, index) in featured"
                    :key="item.id"
                    class="block"
                    :class="{ first_news__featured: index === 0 }"
                >
                    <NewsCard :item="item" :icons="config.icons" />
                </li>
            </ul>
        </section>

        <div v-if="config.ctaHtml" class="grid-wrapper" v-html="config.ctaHtml"></div>

        <section v-if="more.length" class="grid-wrapper archive__more margin--3xl">
            <ul ref="list" class="archive__more__holder block__holder grid three">
                <li v-for="item in more" :key="item.id" class="block">
                    <NewsCard :item="item" :icons="config.icons" />
                </li>
            </ul>

            <ResourceLoadMore
                v-if="hasMore || loading"
                :loading="loading"
                :next-url="nextUrl"
                :label="config.i18n.loadMore"
                @load="loadMore"
            />
        </section>

    </template>

    <div v-else class="grid-wrapper margin--3xl">
        <div class="search-results__empty" role="status">
            <span v-html="config.icons.info"></span>
            <h5 class="body--large">
                {{ config.i18n.noResults }}
            </h5>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, reactive, ref } from 'vue'

import NewsFilters from './NewsFilters.vue'
import NewsCard from './NewsCard.vue'
import ResourceLoadMore from '../resources/ResourceLoadMore.vue'

const props = defineProps({
    config: { type: Object, required: true },
})

const config = props.config
const featuredCount = config.featuredCount || 4

const filters = reactive({
    category: config.filters.category || '',
    subcategory: config.filters.subcategory || '',
    search: config.filters.search || '',
})

const items = ref(config.items || [])
const total = ref(config.total || 0)
const loading = ref(false)
const list = ref(null)

let request = null

const featured = computed(() => items.value.slice(0, featuredCount))
const more = computed(() => items.value.slice(featuredCount))

const hasFilters = computed(
    () => filters.category !== '' || filters.subcategory !== '' || filters.search !== ''
)

const hasMore = computed(() => items.value.length < total.value)

const countLabel = computed(() => {
    const template = total.value === 1 ? config.i18n.showingOne : config.i18n.showingMany

    return (template || '').replace('%s', total.value.toLocaleString())
})

const queryString = (overrides = {}) => {
    const state = { ...filters, ...overrides }
    const params = new URLSearchParams()

    if (state.category) {
        params.set('news_category', state.category)
    }

    if (state.subcategory) {
        params.set('news_subcategory', state.subcategory)
    }

    if (state.search) {
        params.set('news_search', state.search)
    }

    const query = params.toString()

    return query ? `?${query}` : ''
}

const buildUrl = (overrides = {}) => config.pageUrl + queryString(overrides)

const nextUrl = computed(() => {
    const base = config.pageUrl.replace(/\/?$/, '/')

    return `${base}page/2/${queryString()}`
})

const fetchItems = async ({ append }) => {
    if (request) {
        request.abort()
    }

    request = new AbortController()
    loading.value = true

    const offset = append ? items.value.length : 0
    const perPage = append ? config.perPage : featuredCount + config.perPage

    const params = new URLSearchParams({
        category: filters.category,
        subcategory: filters.subcategory,
        search: filters.search,
        offset: String(offset),
        per_page: String(perPage),
    })

    if (config.lang) {
        params.set('lang', config.lang)
    }

    try {
        const response = await fetch(`${config.restUrl}?${params.toString()}`, {
            signal: request.signal,
            headers: { Accept: 'application/json' },
        })

        if (!response.ok) {
            throw new Error(`Request failed with ${response.status}`)
        }

        const data = await response.json()
        const previous = more.value.length

        items.value = append ? items.value.concat(data.items) : data.items
        total.value = data.total

        if (append) {
            await nextTick()
            list.value?.querySelectorAll('.news__block')[previous]?.focus({ preventScroll: true })
        }

        loading.value = false
        request = null
    } catch (error) {
        if (error.name !== 'AbortError') {
            loading.value = false
            request = null
        }
    }
}

const commit = () => {
    window.history.pushState({ ...filters }, '', buildUrl())

    fetchItems({ append: false })
}

const findParent = (subcategory) =>
    config.categories.find((parent) => (parent.children || []).some((child) => child.slug === subcategory))

const applyCategory = (value) => {
    if (filters.category === value) {
        return
    }

    filters.category = value

    if (!value || findParent(filters.subcategory)?.slug !== value) {
        filters.subcategory = ''
    }

    commit()
}

const applySubcategory = (value) => {
    if (filters.subcategory === value) {
        return
    }

    filters.subcategory = value

    if (value) {
        filters.category = findParent(value)?.slug || filters.category
    }

    commit()
}

const applyFilter = (key, value) => {
    if (key === 'search') {
        value = (value || '').trim().replace(/\s+/g, ' ')
    }

    if (filters[key] === value) {
        return
    }

    filters[key] = value

    commit()
}

const reset = () => {
    if (!hasFilters.value) {
        return
    }

    filters.category = ''
    filters.subcategory = ''
    filters.search = ''

    window.history.pushState({ ...filters }, '', config.pageUrl)

    fetchItems({ append: false })
}

const loadMore = () => {
    if (loading.value || !hasMore.value) {
        return
    }

    fetchItems({ append: true })
}

const onPopState = () => {
    const params = new URLSearchParams(window.location.search)

    filters.category = params.get('news_category') || ''
    filters.subcategory = params.get('news_subcategory') || ''
    filters.search = (params.get('news_search') || '').trim()

    fetchItems({ append: false })
}

onMounted(() => {
    window.addEventListener('popstate', onPopState)
})

onBeforeUnmount(() => {
    window.removeEventListener('popstate', onPopState)
    request?.abort()
})
</script>
